import React, { useEffect, useState } from "react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiImage,
  FiSearch,
} from "react-icons/fi";

import AdminButton from "../../_shared/ui/AdminButton";
import AdminDrawer from "../../_shared/ui/AdminDrawer";

import {
  searchMedia,
  toAbsolutePublicUrl,
} from "../../../../services/mediaLibraryServices";

export default function TrombinosMediaPickerDrawer({
  open,
  onClose,
  onSelect,
}) {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("trombinos");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    const fetchMedia = async () => {
      try {
        setLoading(true);

        const res = await searchMedia({
          q: q.trim() || undefined,
          category: category.trim() || undefined,
          type: "image",
          page,
          limit,
          sort: "created_at",
          dir: "DESC",
        });

        if (cancelled) return;

        setItems(Array.isArray(res?.items) ? res.items : []);
        setTotalPages(Number(res?.totalPages || 1));
      } catch (error) {
        console.error("Erreur searchMedia trombinos :", error);

        if (!cancelled) {
          setItems([]);
          setTotalPages(1);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchMedia();

    return () => {
      cancelled = true;
    };
  }, [open, q, category, page]);

  const handleSelect = (item) => {
    if (!item) return;
    onSelect?.(item);
  };

  const goPrev = () => setPage((current) => Math.max(current - 1, 1));
  const goNext = () =>
    setPage((current) => Math.min(current + 1, totalPages));

  return (
    <AdminDrawer
      open={open}
      onClose={onClose}
      title="Choisir une photo"
      subtitle="Sélectionnez une image déjà présente dans la photothèque."
      icon={FiImage}
      size="xl"
      footer={
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2 text-sm font-bold text-slate-500 dark:text-slate-400">
            <AdminButton
              icon={FiChevronLeft}
              variant="secondary"
              size="sm"
              disabled={page <= 1}
              onClick={goPrev}
            >
              Précédent
            </AdminButton>

            <span>
              Page {page} / {totalPages}
            </span>

            <AdminButton
              icon={FiChevronRight}
              variant="secondary"
              size="sm"
              disabled={page >= totalPages}
              onClick={goNext}
            >
              Suivant
            </AdminButton>
          </div>

          <AdminButton variant="secondary" onClick={onClose}>
            Fermer
          </AdminButton>
        </div>
      }
    >
      <div className="space-y-5">
        <section className="rounded-[1.7rem] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
          <div className="grid gap-4 md:grid-cols-[1fr_220px]">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
                Rechercher
              </label>

              <div className="mt-2 flex min-h-12 items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 transition focus-within:border-sky-400 focus-within:bg-white focus-within:ring-4 focus-within:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:focus-within:border-sky-500 dark:focus-within:ring-sky-500/10">
                <FiSearch className="text-slate-400" />

                <input
                  value={q}
                  onChange={(event) => {
                    setPage(1);
                    setQ(event.target.value);
                  }}
                  placeholder="Nom, tag, fichier..."
                  className="h-11 min-w-0 flex-1 bg-transparent text-sm font-medium text-slate-800 outline-none placeholder:text-slate-400 dark:text-slate-100"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
                Catégorie
              </label>

              <input
                value={category}
                onChange={(event) => {
                  setPage(1);
                  setCategory(event.target.value);
                }}
                placeholder="trombinos"
                className="mt-2 h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700 outline-none transition focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:focus:border-sky-500 dark:focus:ring-sky-500/10"
              />
            </div>
          </div>
        </section>

        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <div
                key={index}
                className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950"
              >
                <div className="aspect-square animate-pulse bg-slate-200 dark:bg-slate-800" />
                <div className="space-y-2 p-3">
                  <div className="h-4 w-3/4 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
                  <div className="h-3 w-1/2 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
                </div>
              </div>
            ))}
          </div>
        ) : !items.length ? (
          <div className="rounded-[1.7rem] border border-slate-200 bg-white p-8 text-center dark:border-slate-800 dark:bg-slate-950">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-2xl text-slate-400 dark:bg-slate-800 dark:text-slate-500">
              <FiImage />
            </div>

            <p className="mt-4 text-sm font-bold text-slate-700 dark:text-slate-200">
              Aucune image trouvée.
            </p>

            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Vérifiez la catégorie ou ajoutez des photos dans la photothèque.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {items.map((media) => {
              const url = toAbsolutePublicUrl(media.url);

              return (
                <button
                  key={media.id}
                  type="button"
                  onClick={() => handleSelect(media)}
                  className="group overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white text-left transition hover:-translate-y-0.5 hover:border-sky-300 hover:shadow-lg dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-500/30"
                >
                  <div className="aspect-square overflow-hidden bg-slate-100 dark:bg-slate-800">
                    <img
                      src={url}
                      alt={media.alt_text || media.title || media.file_name}
                      className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                    />
                  </div>

                  <div className="p-3">
                    <p className="line-clamp-2 text-sm font-bold text-slate-800 dark:text-slate-100">
                      {media.title || "Sans titre"}
                    </p>

                    <p className="mt-1 line-clamp-1 text-xs font-medium text-slate-500 dark:text-slate-400">
                      {media.category || "Sans catégorie"}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </AdminDrawer>
  );
}